"use client";

import { useSearchParams } from "next/navigation";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import LoadingOverlay from "../components/ui/LoadingOverlay";
import { usePathname } from "../i18n/navigation";

const LoadingContext = createContext();

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error("useLoading must be used within a LoadingProvider");
  }
  return context;
};

export const LoadingProvider = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [customComponent, setCustomComponent] = useState(null);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Pathname ve searchParams değişikliklerini dinle ve sayfa değişikliklerinde loader'ı kapat
  useEffect(() => {
    if (isVisible) {
      setIsVisible(false);
      setCustomComponent(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams]);

  const show = useCallback((component = null) => {
    setCustomComponent(component);
    setIsVisible(true);
  }, []);

  const hide = useCallback(() => {
    setIsVisible(false);
    setCustomComponent(null);
  }, []);

  const showWithComponent = useCallback(
    (component) => {
      show(component);
    },
    [show],
  );

  const withLoading = useCallback(
    async (asyncFunction, component = null) => {
      try {
        show(component);
        await asyncFunction();
      } finally {
        hide();
      }
    },
    [show, hide],
  );

  // Value objesini useMemo ile cache'le - gereksiz re-render'ları önle
  const value = useMemo(
    () => ({
      isVisible,
      show,
      hide,
      showWithComponent,
      withLoading,
    }),
    [isVisible, show, hide, showWithComponent, withLoading],
  );

  return (
    <LoadingContext.Provider value={value}>
      {children}
      <LoadingOverlay isVisible={isVisible} customComponent={customComponent} />
    </LoadingContext.Provider>
  );
};
